// ----------------------------------------------------------------------
// Shared Section Types
// ----------------------------------------------------------------------

import type { IconifyName } from 'src/components/iconify/register-icons';

import contactsAsset from '../assets/icons/contacts.svg';
import outlookAsset from '../assets/icons/microsoft-outlook.svg';
import outlookCalendarAsset from '../assets/icons/outlook-calendar.svg';

// Skill record from backend
export interface SkillRecord {
  id: string;
  name: string;
  description: string | null;
  instructions: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Connector types
export interface Connector {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  isConnected: boolean;
  isLoading?: boolean;
  isImplemented?: boolean;
}

// Connector record from backend
export interface ConnectorRecord {
  id: string;
  name: string;
  description: string | null;
  type: string;
  config: Record<string, unknown>;
  tags: string[];
  requires_token: boolean;
  configurable?: boolean;
  iconify_logo: string | null;
  auth0_connection: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const CONNECTOR_BRAND_COLORS: Record<string, string> = {
  microsoft_outlook: '#0A2472',
  microsoft_outlook_calendar: '#0A2472',
  microsoft_outlook_emails: '#0A2472',
  microsoft_teams: '#312e81',
  microsoft_onedrive: '#0A2472',
  microsoft_sharepoint: '#0A2472',
  microsoft_contacts: '#1E293B',
  google_calendar: '#1e293b',
  google_gmail: '#1e293b',
  google_contacts: '#1e293b',
};

export function getConnectorBgColor(connectorId: string, fallback: string): string {
  return CONNECTOR_BRAND_COLORS[connectorId] ?? fallback;
}

export function getConnectorIcon(connector: Pick<ConnectorRecord, 'id' | 'iconify_logo'>): IconifyName {
  // Outlook connectors render via a static SVG asset (see connectorAssetUrl);
  // iconify_logo only kicks in when the connector explicitly opts in.
  if (connector.iconify_logo && !connector.id.startsWith('microsoft_outlook'))
    return connector.iconify_logo as IconifyName;
  if (connector.id === 'microsoft_teams') return 'logos:microsoft-teams';
  return connector.id.startsWith('microsoft_') ? 'logos:microsoft-icon' : 'logos:google-icon';
}

// Static SVG assets that should be rendered via <img> instead of Iconify —
// keeps the Outlook 2025 logo identical to the one used in Email / Calendar
// scene headers. Imported rather than hardcoded as '/icons/*.svg' so each
// consuming app's bundler resolves the URL: apps/ari-cloud/public/icons shipped
// only microsoft-outlook.svg, so the contacts and outlook-calendar literals fell
// through to its SPA index fallback and rendered as broken images.
export function getConnectorAssetUrl(connectorId: string): string | null {
  if (connectorId === 'microsoft_outlook_calendar') return outlookCalendarAsset;
  if (connectorId === 'microsoft_contacts') return contactsAsset;
  if (connectorId.startsWith('microsoft_outlook')) return outlookAsset;
  return null;
}

export enum ConnectorTokenStatus {
  Valid = 'valid',
  Expired = 'expired',
  Error = 'error',
}

export type ConnectorState = 'connected' | 'expired' | 'disconnected' | 'error';

/**
 * A connector is "connected" only when:
 *   - the user has it enabled (their intent), AND
 *   - if it requires an upstream token: the connector status endpoint reports it as live AND
 *     the last known token_status is `valid`.
 *
 * Connectors with `requires_token=false` (local/MCP-style) skip the upstream
 * checks entirely — they don't have an upstream token that can expire.
 *
 * For token-requiring connectors, rows in `user_connectors` are inserted with
 * `token_status='valid'` (the column default and what `callbackHandler` writes
 * after a successful OAuth callback). A missing entry in the statuses map
 * defaults to `valid` — that case happens for newly-enabled rows the catalog
 * query has already returned but `user_connector_statuses` hasn't been
 * populated for (brief race between optimistic enable and catalog refetch).
 */
export function isConnectorConnected(
  connector: Pick<ConnectorRecord, 'id' | 'requires_token'>,
  connectedConnectorIds: Set<string>,
  userEnabledConnectorIds: Set<string>,
  userConnectorStatuses: Map<string, ConnectorTokenStatus>
): boolean {
  if (!userEnabledConnectorIds.has(connector.id)) return false;
  if (!connector.requires_token) return true;
  if (!connectedConnectorIds.has(connector.id)) return false;
  const status = userConnectorStatuses.get(connector.id) ?? ConnectorTokenStatus.Valid;
  return status === ConnectorTokenStatus.Valid;
}

/**
 * Resolves a connector's UI state into one of four buckets so the view can
 * render distinct affordances (connected dot, Reconnect button, "couldn't verify"
 * message, or unconnected/Connect button).
 *
 * Connectors with `requires_token=false` (local/MCP-style) only have two
 * states: enabled → 'connected', disabled → 'disconnected'. They have no
 * upstream token, so 'expired' / 'error' don't apply.
 */
export function getConnectorState(
  connector: Pick<ConnectorRecord, 'id' | 'requires_token'>,
  connectedConnectorIds: Set<string>,
  userEnabledConnectorIds: Set<string>,
  userConnectorStatuses: Map<string, ConnectorTokenStatus>
): ConnectorState {
  if (!userEnabledConnectorIds.has(connector.id)) return 'disconnected';
  if (!connector.requires_token) return 'connected';

  const status = userConnectorStatuses.get(connector.id) ?? ConnectorTokenStatus.Valid;
  if (status === ConnectorTokenStatus.Expired) return 'expired';
  if (status === ConnectorTokenStatus.Error) return 'error';

  // status === 'valid' still requires the connector-status signal.
  if (!connectedConnectorIds.has(connector.id)) return 'expired';

  return 'connected';
}

// Chat types
export interface ChatSummary {
  id: string;
  title: string | null;
  updated_at: string;
  messageCount?: number;
  device_type?: string;
  /**
   * Optimistic rows are non-interactive (no navigation, no delete) until the
   * backend refetch replaces them with confirmed data.
   */
  isOptimistic?: boolean;
  /** Title not yet server-confirmed — sidebar shows a pending spinner. */
  titlePending?: boolean;
}
